import React from 'react';
import InputErrorMessage from '../../lego/InputErrorMessage';
import InputHint from '../../lego/InputHint';
import InputLabelSubText from '../../lego/InputLabelSubText';
import InputLabelText from '../../lego/InputLabelText';
import InputSuccessMessage from '../../lego/InputSuccessMessage';
import FormElement, { FormElementProps } from '../../layouts/FormElement';
import TagInputField, { TagInputFieldProps } from '../../lego/TagInputField';

export interface Props
  extends TagInputFieldProps,
    Pick<FormElementProps, 'isLabelHidden' | 'noBottomSpacing' | 'className'> {
  /** Required for a11y reasons. We need to be able to link autocomplete menus and labels to the input field. */
  id: string;

  /**
   * Displays above the input field.
   *
   * Required for a11y reasons. If design won't work a label into the spec, consider passing through `isLabelHidden`.
   */
  label: string;

  /** Displays below the `label` in smaller font */
  subLabel?: string;

  /**
   * Displays below the input field.
   *
   * Depending on the value of `validationResult`, it'll be wrapped in...
   *
   * `error`: `<InputErrorMessage />`
   *
   * `success`: `<InputSuccessMessage />`
   *
   * `undefined`: `<InputHint />`
   *
   */
  details?: React.ReactNode;

  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TagInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      label,
      isLabelHidden,
      noBottomSpacing,
      className,
      subLabel,
      details,
      validationResult,
      isRequired,
      ...rest
    },
    ref
  ) => {
    let wrappedDetails: React.ReactNode = null;

    const errorMessageId = `${id}-error`;
    const hintId = `${id}-hint`;

    switch (validationResult) {
      case 'success':
        wrappedDetails = <InputSuccessMessage>{details}</InputSuccessMessage>;
        break;
      case 'error':
        wrappedDetails = (
          <InputErrorMessage id={errorMessageId}>{details}</InputErrorMessage>
        );
        break;
      default:
        wrappedDetails = <InputHint id={hintId}>{details}</InputHint>;
    }

    return (
      <FormElement
        className={className}
        inputId={id}
        label={<InputLabelText isRequired={isRequired}>{label}</InputLabelText>}
        isLabelHidden={isLabelHidden}
        subLabel={subLabel && <InputLabelSubText>{subLabel}</InputLabelSubText>}
        details={details && wrappedDetails}
        noBottomSpacing={noBottomSpacing}
      >
        <TagInputField
          id={id}
          ref={ref}
          isRequired={isRequired}
          validationResult={validationResult}
          aria-errormessage={errorMessageId}
          aria-describedby={
            validationResult === undefined && details ? hintId : undefined
          }
          autoComplete="off"
          {...rest}
        />
      </FormElement>
    );
  }
);
TagInput.displayName = 'TagInput';

export default TagInput;
